import { t } from '@lingui/core/macro';
import type { CheckResult } from '@transpordiamet/shared';
import { Check, RotateCcw, AlertTriangle } from 'lucide-react';

interface CheckConfirmationDialogProps {
  checkResult: CheckResult;
  expectedVin?: string | null;
  onConfirm: () => void;
  onRetake: () => void;
  confirming?: boolean;
}

export function CheckConfirmationDialog({
  checkResult,
  expectedVin,
  onConfirm,
  onRetake,
  confirming = false,
}: CheckConfirmationDialogProps) {
  const isFailed = !checkResult.passed;
  const isVinCheck = checkResult.type === 'vin';
  const isVinMismatch = isVinCheck && isFailed && checkResult.error === 'VIN_MISMATCH';
  const isCarCheck = checkResult.type === 'is_car';

  // Display values
  const checkValue = String(checkResult.value || '');

  // Get appropriate title and description based on check type and status
  const getTitle = () => {
    if (!isFailed && isVinCheck) return t`Confirm VIN Code`;
    if (isVinMismatch) return t`VIN does not match`;
    if (isFailed && isVinCheck) return t`VIN Extraction Issue`;
    if (isFailed && isCarCheck) return t`Vehicle Detection Issue`;
    return t`Check Result`;
  };

  const getDescription = () => {
    if (!isFailed && isVinCheck) {
      return t`Please verify that the VIN code above is correct. If it's incorrect, you can retake the photo.`;
    }
    if (isVinMismatch) {
      return t`The VIN in the photo does not match the VIN for this session. Confirm anyway saves this photo.`;
    }
    if (isFailed && isVinCheck) {
      return t`The VIN code could not be clearly extracted from the photo. You can retake the photo for better results, or confirm to proceed anyway.`;
    }
    if (isFailed && isCarCheck) {
      return t`A vehicle could not be detected in the photo. You can retake the photo to capture the vehicle clearly, or confirm to proceed anyway.`;
    }
    return t`Please review the check result and choose to retake or confirm.`;
  };

  const getConfirmButtonText = () => {
    if (isFailed) return t`Confirm Anyway`;
    if (isVinCheck) return t`Confirm VIN`;
    return t`Confirm`;
  };

  return (
    <div className="capture-overlay bg-black/80 flex items-center justify-center p-2">
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full p-4 max-h-full overflow-y-auto"
        style={{ touchAction: 'pan-y' }}
      >
        {/* Title */}
        <div className="flex items-center gap-2 mb-3">
          {isFailed && <AlertTriangle className="w-5 h-5 shrink-0 text-yellow-500" />}
          <h2 className="text-lg leading-tight font-bold text-gray-900">{getTitle()}</h2>
        </div>

        {/* Value Display (only for successful VIN extraction or failed checks with values) */}
        {isVinMismatch && expectedVin && (
          <div className="bg-gray-100 rounded-lg px-3 py-2 mb-2">
            <p className="text-xs text-gray-600 mb-0.5">{t`Session VIN:`}</p>
            <p className="text-lg font-mono font-bold text-gray-900 tracking-wide break-all">{expectedVin}</p>
          </div>
        )}

        {((!isFailed && isVinCheck) || (isFailed && checkValue)) && (
          <div
            className={`${isFailed ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-100'} rounded-lg px-3 py-2 mb-2`}
          >
            {!isFailed && isVinCheck && <p className="text-xs text-gray-600 mb-0.5">{t`Extracted VIN:`}</p>}
            {isFailed && isVinCheck && <p className="text-xs text-yellow-700 mb-0.5">{t`Extracted value:`}</p>}
            <p className="text-lg font-mono font-bold text-gray-900 tracking-wide break-all">{checkValue}</p>
          </div>
        )}

        {/* Description */}
        <p className={`${isFailed ? 'text-gray-800' : 'text-gray-700'} text-sm mb-4`}>{getDescription()}</p>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {/* Retake button */}
          <button
            onClick={onRetake}
            disabled={confirming}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-4 h-4" />
            <span>{t`Retake Photo`}</span>
          </button>

          {/* Confirm button */}
          <button
            onClick={onConfirm}
            disabled={confirming}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm ${
              isFailed ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
            } text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirming ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>{t`Confirming...`}</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>{getConfirmButtonText()}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
